import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/authService';
import { ApiService, NotificationDto } from '../services/api';
import { useAuth } from './AuthContext';

type NotificationsState = {
  notifications: NotificationDto[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
};

type NotificationsContextValue = NotificationsState & {
  refresh: (opts?: { limit?: number; unreadOnly?: boolean }) => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (opts?: { limit?: number; unreadOnly?: boolean }) => {
    const token = authService.getAccessToken();
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const limit = opts?.limit ?? 20;
      const page = 1;
      const [listResp, unreadResp] = await Promise.all([
        ApiService.listNotifications(token, { page, limit, unreadOnly: Boolean(opts?.unreadOnly) }),
        ApiService.listNotifications(token, { page: 1, limit: 1, unreadOnly: true }),
      ]);
      setNotifications(listResp.data || []);
      const count =
        (typeof unreadResp.unreadCount === 'number' ? unreadResp.unreadCount : undefined) ??
        (typeof unreadResp.pagination?.total === 'number' ? unreadResp.pagination.total : 0);
      setUnreadCount(count);
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const markRead = async (id: string) => {
    const token = authService.getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await ApiService.markNotificationRead(token, id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e: any) {
      setError(e?.message || 'Failed to mark notification as read');
      throw e;
    }
  };

  const markAllRead = async () => {
    const token = authService.getAccessToken();
    if (!token) return;
    setError(null);
    try {
      await ApiService.markAllNotificationsRead(token);
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
      setUnreadCount(0);
    } catch (e: any) {
      setError(e?.message || 'Failed to mark all notifications as read');
      throw e;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      return;
    }
    // Load initial unread count + latest items
    void refresh({ limit: 20 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const value = useMemo<NotificationsContextValue>(
    () => ({ notifications, unreadCount, isLoading, error, refresh, markRead, markAllRead }),
    [notifications, unreadCount, isLoading, error],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

